import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { formatMoneyFromUnknown, resolveCurrencyCode } from '@/lib/money'
import * as customerService from '../../services/customerService'
import { Card, CardHeader } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { SkeletonDetailFields, SkeletonListPage } from '../../components/ui/Skeleton'
import { LoadMoreFooter } from '../../components/ui/LoadMoreFooter'
import { getUserFacingErrorMessage } from '../../utils/userFacingError'
import { formatStandardAddress } from '@/lib/formatStandardAddress'
import { useCurrentStore } from '../../context/CurrentStoreContext'

const ORDERS_LIMIT = 10

function formatDateTime(iso: string) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    })
  } catch {
    return iso
  }
}

function formatAddressLine(addr: customerService.CustomerAddress) {
  return formatStandardAddress(addr)
}

export default function CustomerDetail() {
  const { currentStoreId: storeId } = useCurrentStore()
  const { id } = useParams()
  const customerId = Number(id)
  const validId = Number.isInteger(customerId) && customerId > 0

  const [customer, setCustomer] = useState<customerService.CustomerDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [ordersPage, setOrdersPage] = useState(1)
  const [ordersLoading, setOrdersLoading] = useState(false)
  const [ordersLoadingMore, setOrdersLoadingMore] = useState(false)

  const loadCustomer = useCallback(
    async (page: number, appendOrders: boolean) => {
      if (!validId) return
      if (appendOrders) setOrdersLoadingMore(true)
      else if (page === 1) setLoading(true)
      else setOrdersLoading(true)
      setError(null)
      try {
        const data = await customerService.fetchCustomerById(customerId, {
          page,
          limit: ORDERS_LIMIT,
        })
        setCustomer((prev) => {
          if (!appendOrders || !prev) return data
          return {
            ...data,
            orders: {
              ...data.orders,
              items: [...prev.orders.items, ...data.orders.items],
            },
          }
        })
      } catch (e) {
        if (!appendOrders) setCustomer(null)
        setError(getUserFacingErrorMessage(e, 'Failed to load customer'))
      } finally {
        setLoading(false)
        setOrdersLoading(false)
        setOrdersLoadingMore(false)
      }
    },
    [customerId, validId, storeId],
  )

  useEffect(() => {
    setOrdersPage(1)
    void loadCustomer(1, false)
  }, [loadCustomer, storeId])

  const orders = customer?.orders.items ?? []
  const ordersTotal = customer?.orders.total ?? 0
  const hasMoreOrders = orders.length < ordersTotal

  const loadMoreOrders = () => {
    const next = ordersPage + 1
    setOrdersPage(next)
    void loadCustomer(next, true)
  }

  if (!validId) {
    return (
      <Card>
        <CardHeader title="Customer not found" description="Invalid customer id." />
        <Link to="/customers" className="text-sm text-primary hover:underline">
          Back to customers
        </Link>
      </Card>
    )
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <SkeletonDetailFields fields={6} />
        <SkeletonListPage toolbarFields={0} columns={5} rows={4} />
      </div>
    )
  }

  if (error || !customer) {
    return (
      <Card>
        <CardHeader title="Customer" description={error ?? 'Customer not found.'} />
        <Link to="/customers" className="text-sm text-primary hover:underline">
          Back to customers
        </Link>
      </Card>
    )
  }

  const currency = resolveCurrencyCode(customer.currency)
  const displayName = customerService.customerDisplayName(customer)

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" size="sm" asChild>
          <Link to="/customers">← Customers</Link>
        </Button>
      </div>

      <Card>
        <CardHeader title={displayName} description={customer.email} />
        <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">First name</dt>
            <dd className="mt-1 text-sm">{customer.firstName || '—'}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Last name</dt>
            <dd className="mt-1 text-sm">{customer.lastName || '—'}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Member since</dt>
            <dd className="mt-1 text-sm">{formatDateTime(customer.createdAt)}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Orders</dt>
            <dd className="mt-1 text-sm tabular-nums">{customer.orderCount}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Total spend</dt>
            <dd className="mt-1 text-sm tabular-nums font-medium">
              {formatMoneyFromUnknown(customer.totalSpend, currency)}
            </dd>
          </div>
        </dl>
      </Card>

      <Card>
        <CardHeader title="Saved addresses" description="Delivery addresses saved across your stores." />
        {customer.addresses.length === 0 ? (
          <p className="text-sm text-muted-foreground">No saved addresses yet.</p>
        ) : (
          <ul className="divide-y divide-border rounded-xl border border-border">
            {customer.addresses.map((addr) => (
              <li key={addr.id} className="px-4 py-4 text-sm">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="font-medium">
                      {addr.label}
                      {addr.isDefault ? (
                        <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                          Default
                        </span>
                      ) : null}
                    </p>
                    <p className="mt-1 text-muted-foreground">{addr.name}</p>
                    <p className="mt-1">{formatAddressLine(addr)}</p>
                    {addr.phone ? <p className="mt-1 text-muted-foreground">{addr.phone}</p> : null}
                    {addr.deliveryNotes ? (
                      <p className="mt-1 text-xs text-muted-foreground">Notes: {addr.deliveryNotes}</p>
                    ) : null}
                  </div>
                  {addr.store ? (
                    <span className="text-xs text-muted-foreground">{addr.store.name}</span>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <CardHeader title="Order history" description="Orders placed by this customer." />
        {ordersLoading && orders.length === 0 ? (
          <SkeletonListPage toolbarFields={0} columns={5} rows={4} />
        ) : ordersTotal === 0 ? (
          <p className="text-sm text-muted-foreground">No orders yet.</p>
        ) : (
          <>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="min-w-full divide-y divide-border text-sm">
                <thead className="bg-muted/30 text-left text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">Order #</th>
                    <th className="px-4 py-3 font-medium">Date</th>
                    <th className="px-4 py-3 font-medium">Store</th>
                    <th className="px-4 py-3 font-medium">Total</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border bg-card">
                  {orders.map((order) => (
                    <tr key={order.orderReference}>
                      <td className="px-4 py-3 font-mono text-xs sm:text-sm">{order.orderReference}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-muted-foreground">
                        {formatDateTime(order.createdAt)}
                      </td>
                      <td className="px-4 py-3">{order.store?.name ?? '—'}</td>
                      <td className="px-4 py-3 tabular-nums">
                        {formatMoneyFromUnknown(order.total, resolveCurrencyCode(order.currency))}
                      </td>
                      <td className="px-4 py-3">
                        {order.status}
                        {order.paymentStatus ? (
                          <span className="ml-1 text-xs text-muted-foreground">({order.paymentStatus})</span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3">
                        <Link
                          to={`/orders/${encodeURIComponent(order.orderReference)}`}
                          className="text-primary hover:underline"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <LoadMoreFooter
              shown={orders.length}
              total={ordersTotal}
              hasMore={hasMoreOrders}
              loading={ordersLoading}
              loadingMore={ordersLoadingMore}
              onLoadMore={loadMoreOrders}
            />
          </>
        )}
      </Card>
    </div>
  )
}
